import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SocialMediaPost, SocialPostStatus } from '@/lib/api';
import { Edit, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const PLATFORM_LABELS: Record<string, string> = {
  twitter: 'X / Twitter',
  linkedin: 'LinkedIn',
  facebook: 'Facebook',
  instagram: 'Instagram',
};

const STATUS_CLASSES: Record<SocialPostStatus, string> = {
  draft: 'border-transparent bg-muted text-muted-foreground',
  scheduled: 'border-transparent bg-blue-100 text-blue-700',
  publishing: 'border-transparent bg-yellow-100 text-yellow-700',
  published: 'border-transparent bg-green-100 text-green-700',
  failed: 'border-transparent bg-red-100 text-red-700',
  cancelled: 'border-transparent bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<SocialPostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

type SortDir = 'asc' | 'desc';

interface PostsTableProps {
  posts: SocialMediaPost[];
}

function getPublishResult(post: SocialMediaPost): { label: string; isError: boolean } {
  for (const result of Object.values(post.publishResults)) {
    if (result.error) return { label: result.error, isError: true };
  }
  const hasSuccess = Object.values(post.publishResults).some((r) => r.success);
  if (hasSuccess) return { label: 'Published', isError: false };
  return { label: '—', isError: false };
}

export function PostsTable({ posts }: PostsTableProps) {
  const navigate = useNavigate();
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = [...posts].sort((a, b) => {
    const diff = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return sortDir === 'asc' ? diff : -diff;
  });

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));

  if (posts.length === 0) {
    return null;
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[300px]">
              <button
                className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                onClick={toggleSort}
              >
                Post Content
                {sortDir === 'asc' ? (
                  <ArrowUp className="h-3 w-3" />
                ) : sortDir === 'desc' ? (
                  <ArrowDown className="h-3 w-3" />
                ) : (
                  <ArrowUpDown className="h-3 w-3" />
                )}
              </button>
            </TableHead>
            <TableHead>Platforms</TableHead>
            <TableHead>Accounts</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Result</TableHead>
            <TableHead>Last Updated</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((post) => {
            const platforms = Object.keys(post.platformContent);
            const publishResult = getPublishResult(post);

            return (
              <TableRow
                key={post.postId}
                className="cursor-pointer"
                onClick={() => navigate(`/a/arche_social_media/compose/${post.postId}`)}
              >
                {/* Post Content + created timestamp */}
                <TableCell>
                  <p className="text-sm font-medium line-clamp-2 max-w-[280px]">
                    {post.originalContent || (
                      <span className="text-muted-foreground italic">No content</span>
                    )}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(post.createdAt).toLocaleString()}
                  </p>
                </TableCell>

                {/* Platforms */}
                <TableCell>
                  {platforms.length > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      {platforms.map((p) => (
                        <span key={p} className="text-xs">
                          {PLATFORM_LABELS[p] ?? p}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">Manual</span>
                  )}
                </TableCell>

                {/* Accounts */}
                <TableCell>
                  <p className="text-sm font-medium">{post.targetAccountIds.length}</p>
                  <p className="text-xs text-muted-foreground">
                    {post.targetAccountIds.length === 1 ? 'account' : 'accounts'}
                  </p>
                </TableCell>

                {/* Status */}
                <TableCell>
                  <Badge className={STATUS_CLASSES[post.status]}>
                    {STATUS_LABELS[post.status]}
                  </Badge>
                </TableCell>

                {/* Publish Result */}
                <TableCell className="max-w-[200px]">
                  {publishResult.label === '—' ? (
                    <span className="text-xs text-muted-foreground">—</span>
                  ) : (
                    <span
                      className={`text-xs ${publishResult.isError ? 'text-destructive' : 'text-muted-foreground'}`}
                    >
                      {publishResult.label}
                    </span>
                  )}
                </TableCell>

                {/* Last Updated */}
                <TableCell>
                  <p className="text-sm">{new Date(post.updatedAt).toLocaleDateString()}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(post.updatedAt).toLocaleTimeString()}
                  </p>
                </TableCell>

                {/* Actions */}
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/a/arche_social_media/compose/${post.postId}`)}
                    >
                      <Edit className="h-4 w-4" />
                      <span className="sr-only">Edit</span>
                    </Button>
                    {post.status === 'failed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/a/arche_social_media/compose/${post.postId}`)}
                      >
                        <RefreshCw className="h-4 w-4" />
                        <span className="sr-only">Retry</span>
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
